// Pickup Task Status Enum - 与后端PickupEnums.kt保持一致
export enum PickupTaskStatus {
    DRAFT = 'DRAFT',
    PUBLISHED = 'PUBLISHED',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    CANCELLED = 'CANCELLED'
}

/**
 * 揽收任务起始点类型
 */
export enum PickupTaskStartPointType {
    /** Hub（仓库） */
    HUB = 1,
    /** 中继点 */
    RALLY_POINT = 2,
    /** 第一个预约单地址 */
    FIRST_APPOINTMENT = 3,
    /** 仓库 */
    WAREHOUSE = 4
}

/**
 * 揽收任务终点类型
 */
export enum PickupTaskEndPointType {
    /** Hub（仓库） */
    HUB = 1,
    /** 中继点 */
    RALLY_POINT = 2
}

/**
 * 起始点类型选项
 */
export const START_POINT_TYPE_OPTIONS = [
    {label: 'Hub', value: PickupTaskStartPointType.HUB},
    {label: 'Rally Point', value: PickupTaskStartPointType.RALLY_POINT},
    {label: 'First Appointment', value: PickupTaskStartPointType.FIRST_APPOINTMENT},
    {label: 'Warehouse', value: PickupTaskStartPointType.WAREHOUSE}
];

/**
 * 终点类型选项
 */
export const END_POINT_TYPE_OPTIONS = [
    {label: 'Hub', value: PickupTaskEndPointType.HUB}
];

/**
 * 获取起始点类型描述
 */
export function getStartPointTypeLabel(type: number): string {
    switch (type) {
        case PickupTaskStartPointType.HUB:
            return 'Hub';
        case PickupTaskStartPointType.RALLY_POINT:
            return 'Rally Point';
        case PickupTaskStartPointType.FIRST_APPOINTMENT:
            return 'First Appointment';
        case PickupTaskStartPointType.WAREHOUSE:
            return 'Warehouse';
        default:
            return 'Unknown';
    }
}

/**
 * 获取终点类型描述
 */
export function getEndPointTypeLabel(type: number): string {
    switch (type) {
        case PickupTaskEndPointType.HUB:
            return 'Hub';
        default:
            return 'Unknown';
    }
}

// Vehicle Model Type
export interface VehicleModelType {
    id: string;
    name: string;
    capacity: number;
}

// Vehicle Type
export interface VehicleType {
    id: string;
    plateNumber: string;
    model: VehicleModelType;
    status: string;
}

// Driver Type  
export interface DriverType {
    id: string;
    name: string;
    phone: string;
    email?: string;
}

// Location Type
export interface LocationType {
    id: string;
    name: string;
    address: string;
    zipcode?: string;
}

// Pickup Task Type
export interface PickupTaskType {
    id: string;
    taskNumber: string;
    taskDate: string;
    warehouseId: string;
    warehouseName?: string;
    routeId?: string;
    routeName?: string; // 路线名称快照
    accountId: string;
    driverName: string;
    vehicleId: string;
    vehicleModelId: string;
    vehicleModelName?: string; // 车型名称快照
    vehicleTypeId?: string;
    vehicleTypeName?: string;
    vehicleName?: string; // 车辆名称快照
    vehicleNumber?: string; // 车辆编号快照
    vehicleLicensePlate?: string; // 车牌号快照
    startPointType: number;
    startPointValue: string;
    startPointName?: string;
    endPointType: number;
    endPointValue: string;
    endPointName?: string;
    estimatedDistanceKm?: number;
    estimatedDurationMinutes?: number;
    actualDistanceKm?: number;
    actualDurationMinutes?: number;
    status: PickupTaskStatus;
    expDepartureTime?: number;
    actDepartureTime?: number;
    expCompleteTime?: number;
    actCompleteTime?: number;
    expDepartureTimeAt?: string;
    actDepartureTimeAt?: string;
    expCompleteTimeAt?: string;
    actCompleteTimeAt?: string;
    notes?: string;
    appointmentCount?: number;
    completedAppointments?: number;
    quantityByUnit?: string;
    totalPackages?: number;
    collectedPackages?: number;
    loadRate?: string;
    createdAt?: string;
    updatedAt?: string;
}

// Pickup Task Filter Type
export interface PickupTaskFilterType {
    taskStatus?: PickupTaskStatus;
    taskId?: string;
    driverId?: string;
    vehicleModelId?: string;
    vehicleId?: string;
    warehouseId?: string;
    origin?: string;
    destination?: string;
    taskDateStart?: string;
    taskDateEnd?: string;
}

// Pickup Task Filter Type - 专门用于筛选，不继承PickupTaskType
export interface PickupTaskTypeWithFilter {
    // 基本筛选字段
    id?: string;
    taskNumber?: string;
    status?: PickupTaskStatus;
    accountId?: string;
    vehicleId?: string;
    vehicleModelId?: string;
    warehouseId?: string;
    routeId?: number; // 新增：路线ID
    cooperationWarehouseId?: string; // 揽收站点ID
    zipcode?: string; // 邮编模糊
    zipcodeList?: string[]; // 邮编精确列表
    address?: string; // 地址搜索

    // 日期范围筛选
    taskDateStart?: string;
    taskDateEnd?: string;

    // 起点终点筛选 - UI显示用
    startPointName?: string;
    endPointName?: string;

    // 起点终点筛选 - 后端查询用
    startPointType?: number;
    startPointValue?: string;
    endPointType?: number;
    endPointValue?: string;
}

// Pickup Task Query Variables - 与后端PickupTaskFilterInput匹配
export interface PickupTaskQueryVariables {
    filter: {
        id?: string;
        taskNumber?: string;
        status?: PickupTaskStatus;
        accountId?: string;
        vehicleId?: string;
        vehicleModelId?: string;
        startPointType?: number;
        startPointValue?: string;
        endPointType?: number;
        endPointValue?: string;
        taskDateStart?: string;
        taskDateEnd?: string;
        warehouseId?: string;
        cooperationWarehouseId?: string;
        zipcode?: string;
        zipcodeList?: string[];
        address?: string;
    };
    pageRequest: {
        page: number;
        pageSize: number;
    };
}

// Pickup Task Response
export interface PickupTaskResponse {
    getPickupTasks: {
        data: PickupTaskType[];
        total: string;
        page: number;
        pageSize: number;
    };
} 